import React, { useState } from 'react';

/**
 * PillSelector Component
 *
 * Multi-select or single-select pill buttons.
 * Used for selecting interests, relationships, occasions, etc.
 * Now supports "Other" option with custom text input.
 */

export function PillSelector({
  options,
  selected,
  onChange,
  multiple = false,
  className = '',
  pillClassName = '',
  allowCustom = false,
  customPlaceholder = 'Type your own...',
}) {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const handleSelect = (value) => {
    if (value === '__custom__') {
      setShowCustomInput(true);
      return;
    }

    if (multiple) {
      // Multi-select mode
      const isSelected = selected.includes(value);
      if (isSelected) {
        onChange(selected.filter((v) => v !== value));
      } else {
        onChange([...selected, value]);
      }
    } else {
      // Single-select mode
      onChange(value);
      setShowCustomInput(false);
    }
  };

  const handleCustomSubmit = () => {
    if (customValue.trim()) {
      if (multiple) {
        onChange([...selected, customValue.trim()]);
      } else {
        onChange(customValue.trim());
      }
      setCustomValue('');
      setShowCustomInput(false);
    }
  };

  const handleCustomKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomSubmit();
    } else if (e.key === 'Escape') {
      setShowCustomInput(false);
      setCustomValue('');
    }
  };

  // Check if current selection is a custom value (not in predefined options)
  const predefinedValues = options.map(o => typeof o === 'string' ? o : o.value);
  const isCustomSelected = !multiple && selected && !predefinedValues.includes(selected);

  return (
    <div className={className}>
      <div
        className="flex flex-wrap gap-1.5"
        role={multiple ? 'group' : 'radiogroup'}
        aria-label="Select options"
      >
        {options.map((option) => {
          const value = typeof option === 'string' ? option : option.value;
          const label = typeof option === 'string' ? option : option.label;
          const icon = typeof option === 'object' ? option.icon : null;

          const isSelected = multiple
            ? selected.includes(value)
            : selected === value;

          return (
            <button
              key={value}
              type="button"
              role={multiple ? 'checkbox' : 'radio'}
              aria-checked={isSelected}
              onClick={() => handleSelect(value)}
              className={`
                pill
                ${isSelected ? 'pill-selected' : ''}
                ${pillClassName}
              `.trim().replace(/\s+/g, ' ')}
            >
              {icon && <span>{icon}</span>}
              {label}
            </button>
          );
        })}

        {/* Other/Custom option */}
        {allowCustom && (
          <button
            type="button"
            onClick={() => handleSelect('__custom__')}
            className={`
              pill
              ${isCustomSelected || showCustomInput ? 'pill-selected' : ''}
              ${pillClassName}
            `.trim().replace(/\s+/g, ' ')}
          >
            ✏️ Other
          </button>
        )}
      </div>

      {/* Custom input field - Premium Design */}
      {(showCustomInput || isCustomSelected) && allowCustom && (
        <div className="mt-4 animate-slide-up">
          <div className="relative bg-gradient-to-r from-slate-50 to-white rounded-2xl p-4 border border-slate-200 shadow-sm">
            {/* Decorative accent */}
            <div className="absolute top-0 left-4 right-4 h-0.5 bg-gradient-to-r from-coral-400 via-coral-500 to-coral-400 rounded-full -translate-y-1/2" />

            <div className="flex items-center gap-3">
              {/* Icon */}
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-coral-100 to-coral-50 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">✨</span>
              </div>

              {/* Input */}
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={isCustomSelected && !showCustomInput ? selected : customValue}
                  onChange={(e) => {
                    setCustomValue(e.target.value);
                    if (isCustomSelected && !showCustomInput) {
                      onChange(e.target.value);
                    }
                  }}
                  onKeyDown={handleCustomKeyDown}
                  placeholder={customPlaceholder}
                  autoFocus
                  className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:border-coral-400 focus:ring-4 focus:ring-coral-100 transition-all duration-200 text-base font-medium"
                />
              </div>

              {/* Action Button */}
              {showCustomInput && (
                <button
                  type="button"
                  onClick={handleCustomSubmit}
                  disabled={!customValue.trim()}
                  className={`
                    px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex-shrink-0
                    ${customValue.trim()
                      ? 'bg-gradient-to-r from-coral-500 to-coral-600 text-white shadow-lg shadow-coral-500/30 hover:shadow-xl hover:shadow-coral-500/40 hover:-translate-y-0.5 active:translate-y-0 active:shadow-md'
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                    }
                  `}
                >
                  <span className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                    Add
                  </span>
                </button>
              )}
            </div>

            {/* Helper text */}
            <p className="mt-2 ml-13 text-xs text-slate-400 pl-13">
              Press Enter to add, Escape to cancel
            </p>
          </div>
        </div>
      )}

      {/* Show custom values in multi-select mode - Premium Tags */}
      {multiple && allowCustom && selected.filter(v => !predefinedValues.includes(v)).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selected.filter(v => !predefinedValues.includes(v)).map(customVal => (
            <span
              key={customVal}
              className="group inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-coral-50 to-coral-100 text-coral-700 rounded-xl text-sm font-medium border border-coral-200 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <span className="w-5 h-5 rounded-full bg-coral-200 flex items-center justify-center text-xs">✨</span>
              {customVal}
              <button
                type="button"
                onClick={() => onChange(selected.filter(v => v !== customVal))}
                className="w-5 h-5 rounded-full bg-coral-200 flex items-center justify-center hover:bg-coral-300 transition-colors ml-1"
              >
                <svg className="w-3 h-3 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * InterestsPills - Pre-configured for interests selection
 * Now with custom "Other" option
 */
export function InterestsPills({
  selected = [],
  onChange,
  className = '',
}) {
  const interests = [
    { value: 'coffee', label: 'Coffee', icon: '☕' },
    { value: 'books', label: 'Books', icon: '📚' },
    { value: 'gaming', label: 'Gaming', icon: '🎮' },
    { value: 'fashion', label: 'Fashion', icon: '👗' },
    { value: 'wine', label: 'Wine', icon: '🍷' },
    { value: 'fitness', label: 'Fitness', icon: '🏋️' },
    { value: 'art', label: 'Art', icon: '🎨' },
    { value: 'music', label: 'Music', icon: '🎵' },
    { value: 'plants', label: 'Plants', icon: '🌱' },
    { value: 'cooking', label: 'Cooking', icon: '🍳' },
    { value: 'travel', label: 'Travel', icon: '✈️' },
    { value: 'pets', label: 'Pets', icon: '🐕' },
    { value: 'beauty', label: 'Beauty', icon: '💄' },
    { value: 'wellness', label: 'Wellness', icon: '🧘' },
    { value: 'tech', label: 'Tech', icon: '📱' },
    { value: 'sports', label: 'Sports', icon: '⚽' },
  ];

  return (
    <PillSelector
      options={interests}
      selected={selected}
      onChange={onChange}
      multiple
      allowCustom
      customPlaceholder="Add custom interest..."
      className={className}
    />
  );
}

/**
 * RelationshipPills - Pre-configured for relationship selection
 * Now with custom "Other" option
 */
export function RelationshipPills({
  selected,
  onChange,
  className = '',
}) {
  const relationships = [
    { value: 'family', label: 'Family', icon: '👨‍👩‍👧' },
    { value: 'partner', label: 'Partner', icon: '💑' },
    { value: 'friend', label: 'Friend', icon: '👯' },
    { value: 'colleague', label: 'Colleague', icon: '💼' },
    { value: 'mentor', label: 'Mentor', icon: '🎓' },
  ];

  return (
    <PillSelector
      options={relationships}
      selected={selected}
      onChange={onChange}
      multiple={false}
      allowCustom
      customPlaceholder="e.g., Neighbor, Client..."
      className={className}
    />
  );
}

/**
 * OccasionPills - Pre-configured for occasion selection
 * Now with custom "Other" option
 */
export function OccasionPills({
  selected,
  onChange,
  className = '',
}) {
  const occasions = [
    { value: 'birthday', label: 'Birthday', icon: '🎂' },
    { value: 'anniversary', label: 'Anniversary', icon: '💍' },
    { value: 'christmas', label: 'Christmas', icon: '🎄' },
    { value: 'valentines', label: "Valentine's", icon: '💝' },
    { value: 'graduation', label: 'Graduation', icon: '🎓' },
    { value: 'wedding', label: 'Wedding', icon: '💒' },
    { value: 'baby', label: 'Baby Shower', icon: '👶' },
    { value: 'housewarming', label: 'Housewarming', icon: '🏠' },
    { value: 'thankyou', label: 'Thank You', icon: '🙏' },
  ];

  return (
    <PillSelector
      options={occasions}
      selected={selected}
      onChange={onChange}
      multiple={false}
      allowCustom
      customPlaceholder="e.g., Retirement, Promotion..."
      className={className}
    />
  );
}

/**
 * BudgetPills - Pre-configured for budget selection
 * Now with custom "Other" option
 */
export function BudgetPills({
  selected,
  onChange,
  className = '',
}) {
  const budgets = [
    { value: 'under25', label: 'Under $25' },
    { value: '25-50', label: '$25-50' },
    { value: '50-100', label: '$50-100' },
    { value: '100-200', label: '$100-200' },
    { value: '200-500', label: '$200-500' },
    { value: '500+', label: '$500+' },
  ];

  return (
    <PillSelector
      options={budgets}
      selected={selected}
      onChange={onChange}
      multiple={false}
      allowCustom
      customPlaceholder="e.g., $75, No limit..."
      className={className}
    />
  );
}

export default PillSelector;
